import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";

/*React-Bootstrap components */
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import {
  useDeleteProjectPartner,
  useGetProjectPartnersPagination,
} from "../services/ProjectPartners/projectPartnersAPI";
import { useExportCsv } from "../services/Shared/sharedAPI";
import DataGrid from "./dataGrid/DataGrid";
import DeleteModal from "./ui/DeleteModal";
import RoleGuard from "../auth/RoleGuard";
import { MODULES, ROLE_GROUPS } from "../auth/roles";
import AccessDenied from "./ui/AccessDenied";
import useRoleCheck from "../auth/useRoleCheck";

const PROJECT_PARTNERS_V2_GRID_STATE_KEY = "projectPartnersV2GridState";

const ProjectPartnersV2 = () => {
  const { isEditor } = useRoleCheck(MODULES.PARTNERJI);

  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const [sorting, setSorting] = useState([]);
  const [globalSearch, setGlobalSearch] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [isGridStateHydrated, setIsGridStateHydrated] = useState(false);

  useEffect(() => {
    const rawState = sessionStorage.getItem(PROJECT_PARTNERS_V2_GRID_STATE_KEY);
    if (!rawState) {
      setIsGridStateHydrated(true);
      return;
    }

    try {
      const parsedState = JSON.parse(rawState);
      setSorting(
        Array.isArray(parsedState?.sorting) ? parsedState.sorting : [],
      );
      setGlobalSearch(
        typeof parsedState?.globalSearch === "string"
          ? parsedState.globalSearch
          : "",
      );
      setColumnFilters(
        Array.isArray(parsedState?.columnFilters)
          ? parsedState.columnFilters
          : [],
      );
    } catch {
      setSorting([]);
      setGlobalSearch("");
      setColumnFilters([]);
    }

    setIsGridStateHydrated(true);
  }, []);

  useEffect(() => {
    if (!isGridStateHydrated) return;

    sessionStorage.setItem(
      PROJECT_PARTNERS_V2_GRID_STATE_KEY,
      JSON.stringify({
        sorting,
        globalSearch,
        columnFilters,
      }),
    );
  }, [sorting, globalSearch, columnFilters, isGridStateHydrated]);

  useEffect(() => {
    // Reset page index to 0 when search or sorting changes
    if (!isGridStateHydrated) return;

    setPagination((old) => ({
      ...old,
      pageIndex: 0,
    }));
  }, [globalSearch, sorting, columnFilters, isGridStateHydrated]);

  // For navigation to project partner details
  const navigate = useNavigate();

  // Fetch paginated project partners based on current pagination, sorting, search and filters
  const { data: projectPartners, isLoading } = useGetProjectPartnersPagination(
    pagination,
    sorting,
    globalSearch,
    columnFilters,
  );

  //Fetch filters (šifrants) for the data grid (dropdowns)
  const filters = projectPartners?.filters;

  // Export CSV mutation
  const exportMutation = useExportCsv(
    pagination,
    sorting,
    globalSearch,
    columnFilters,
    "Partnerji/seznamPartnerjevPaginationV2",
  );

  // DELETE project partner
  const {
    mutate: deleteProjectPartner,
    isPending: isDeletePending,
    isError: isDeleteError,
    error: deleteError,
    reset: resetDeleteError,
  } = useDeleteProjectPartner();

  //Set up columns for the data grid, including filters(šifrants) options for dropdown filters
  const columns = React.useMemo(
    () => [
      {
        header: "Naziv partnerja",
        accessorKey: "nazivSlo",
      },
      {
        header: "Naziv partnerja - angleški",
        accessorKey: "nazivEng",
      },
      {
        header: "Država",
        accessorKey: "drzava",
        enableColumnFilter: true,
        meta: {
          filterVariant: "checkboxFilter",
          filterOptions: filters?.drzave || [],
        },
      },
      {
        header: "Kraj",
        accessorKey: "posta",
      },
    ],
    [filters?.drzave, filters],
  );

  //handle navigate by project partner ID
  const handlePodrobnostiClick = (row) => {
    navigate(`/ProjektniPartnerji/Podrobnosti/${row.original.id}`);
  };

  const [partnerToDelete, setPartnerToDelete] = useState(null);

  const handleDeleteClick = (row) => {
    resetDeleteError();
    setPartnerToDelete(row.original);
  };

  const handleConfirmDelete = () => {
    if (!partnerToDelete?.id || isDeletePending) return;

    deleteProjectPartner(partnerToDelete.id, {
      onSuccess: () => {
        resetDeleteError();
        setPartnerToDelete(null);
      },
      onError: () => {
        setTimeout(() => {
          resetDeleteError();
        }, 5000);
      },
    });
  };

  return (
    <Container fluid>
      <RoleGuard
        moduleKey={MODULES.PARTNERJI}
        roles={ROLE_GROUPS.GLOBALNI_AND_FAKULTETNI}
        fallback={<AccessDenied />}
      >
        <>
          <Row>
            <Col className="main-header font-weight-bold">
              Projektni partnerji
            </Col>
          </Row>
          <Row>
            <Col className="header-divider">
              <hr />
            </Col>
          </Row>
          <Row className="mb-2">
            <Col>
              <div className="float-end">
                <RoleGuard
                  moduleKey={MODULES.PARTNERJI}
                  roles={ROLE_GROUPS.EDITORS}
                >
                  <Link
                    className="programs-button-add-text"
                    to="/ProjektniPartnerji/Dodaj/"
                  >
                    <Button variant="outline-primary" size="sm">
                      Dodaj projektnega partnerja
                    </Button>
                  </Link>
                </RoleGuard>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <DataGrid
                data={projectPartners?.data || []}
                columns={columns}
                onPaginationChange={setPagination}
                onSortingChange={setSorting}
                sorting={sorting}
                pagination={pagination}
                columnFilters={columnFilters}
                onColumnFiltersChange={setColumnFilters}
                pageCount={projectPartners?.totalPages || 0}
                loading={isLoading}
                searchGlobalPlaceholder="Iskanje po nazivu, državi ali kraju..."
                hasGlobalSearch={true}
                globalSearch={globalSearch}
                onGlobalSearchChange={setGlobalSearch}
                hasActionColumn={true}
                onPodrobnostiActionClick={handlePodrobnostiClick}
                onDeleteActionClick={handleDeleteClick}
                canDelete={() => isEditor}
                hasExportCsvButton={true}
                exportFileMutation={exportMutation}
              />
            </Col>
          </Row>
        </>
      </RoleGuard>

      <DeleteModal
        show={!!partnerToDelete}
        onHide={() => {
          setPartnerToDelete(null);
          resetDeleteError();
        }}
        onConfirm={handleConfirmDelete}
        text="Ali ste prepričani, da želite izbrisati projektnega partnerja:"
        naziv={partnerToDelete?.nazivSlo}
        isError={isDeleteError}
        errorMessage={"Napaka pri brisanju. Prosimo, poskusite znova."}
        isPending={isDeletePending}
      />
    </Container>
  );
};

export default ProjectPartnersV2;
